/**
 * MutationObserver wrapper that prevents browser extension errors
 * by validating nodes before they can be observed.
 * 
 * Browser extensions often try to observe DOM elements, but sometimes
 * they attempt to observe invalid nodes (null, undefined, or non-Node objects),
 * which causes "Failed to execute 'observe' on 'MutationObserver': parameter 1 is not of type 'Node'" errors.
 * 
 * This wrapper intercepts MutationObserver.observe calls and validates
 * that the target is a valid Node before allowing the observation.
 */

/**
 * Validates that a value is a valid DOM Node that can be safely observed
 */
const VALID_NODE_TYPES = [
  Node.ELEMENT_NODE,
  Node.TEXT_NODE,
  Node.COMMENT_NODE,
  Node.DOCUMENT_NODE,
  Node.DOCUMENT_FRAGMENT_NODE,
  Node.DOCUMENT_TYPE_NODE,
  Node.ATTRIBUTE_NODE,
  Node.PROCESSING_INSTRUCTION_NODE,
  Node.CDATA_SECTION_NODE
];

function isObjectWithNodeType(node: any): boolean {
  return node !== null && node !== undefined && typeof node === 'object' && node.nodeType !== undefined;
}

const isValidNode = (node: any): node is Node => {
  return isObjectWithNodeType(node) && VALID_NODE_TYPES.includes(node.nodeType);
};

/**
 * Original observe method - stored to preserve original behavior
 */
let originalObserve: typeof MutationObserver.prototype.observe | null = null;

/**
 * Flag to track if wrapper has been set up to prevent multiple wrappings
 */
let isWrapped = false;

/**
 * Wrapped observe method that validates nodes before observation
 */
function wrappedObserve(
  this: MutationObserver,
  target: Node,
  options?: MutationObserverInit
): void {
  // Validate that target is a valid Node
  if (!isValidNode(target)) {
    // Silently fail for invalid nodes to prevent browser extension errors
    // This prevents "Failed to execute 'observe' on 'MutationObserver': parameter 1 is not of type 'Node'" errors
    console.warn(
      '[MutationObserverWrapper] Attempted to observe invalid node:',
      target,
      'Skipping observation to prevent errors.'
    );
    return;
  }

  // Call original observe method for valid nodes
  if (originalObserve) {
    originalObserve.call(this, target, options);
  }
}

/**
 * Sets up the MutationObserver wrapper to prevent browser extension errors.
 * This should be called early in the application lifecycle, before any
 * browser extensions have a chance to observe invalid nodes.
 * 
 * This function is idempotent - calling it multiple times has no effect.
 * It also checks if the wrapper was already set up by the inline script in index.html.
 */
function isAlreadyWrapped(): boolean {
  return isWrapped || (typeof window !== 'undefined' && (window as any).__mutationObserverWrapped);
}

function markAsWrapped(): void {
  isWrapped = true;
  if (typeof window !== 'undefined') {
    (window as any).__mutationObserverWrapped = true;
  }
}

export const setupMutationObserverWrapper = (): void => {
  if (isAlreadyWrapped()) return;
  if (typeof MutationObserver === 'undefined') return;

  if (!originalObserve) {
    originalObserve = MutationObserver.prototype.observe;
  }

  MutationObserver.prototype.observe = wrappedObserve;
  markAsWrapped();
};

